import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiCalls from '../../utils/apiCalls';

export function useDeliveryDays(params) {
  const { data, ...rest } = useQuery(
    'deliveryDays',
    () => apiCalls.getDeliveryDays(params).then(({ data: { deliveryDays } }) => deliveryDays),
    {
      refetchInterval: params.refetchInterval,
    }
  );
  return { deliveryDays: data, ...rest };
}

export function useGetDeliveryDayMarketplace() {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    (params) =>
      apiCalls
        .getDeliveryDayMarketplaces(params)
        .then(({ data: { deliveryDayMarketplaces } }) => deliveryDayMarketplaces[0]),
    {
      onSuccess: (ddm) => {
        queryClient.setQueryData(['deliveryDayMarketplace'], ddm);
        queryClient.invalidateQueries('deliveryDays');
      },
    }
  );
  return { getDeliveryDayMarketplace: mutate, ...rest };
}

export function useDeliveryDayMarketplace(deliveryDayMarketplaceId) {
  const { data, ...rest } = useQuery(['deliveryDayMarketplace', { deliveryDayMarketplaceId }], () =>
    apiCalls
      .getDeliveryDayMarketplaces({ deliveryDayMarketplaceId, includeCombinedOrderItems: true })
      .then(({ data: { deliveryDayMarketplaces } }) => deliveryDayMarketplaces[0])
  );
  return { deliveryDayMarketplace: data, ...rest };
}

export function useDeliveryOrderBatches({ deliveryDayId, itemIds }) {
  const { data, ...rest } = useQuery(
    ['deliveryOrderBatches', { deliveryDayId }],
    () =>
      apiCalls
        .getDeliveryOrderBatches({ deliveryDayId, itemIds })
        .then(({ data: { deliveryOrderBatches } }) => deliveryOrderBatches),
    {
      enabled: !!deliveryDayId && !!itemIds,
    }
  );
  return { deliveryOrderBatches: data, ...rest };
}

export function useCombinedOrderItems({ deliveryDayMarketplaceId, excludeOrdersInPacking = false, refetchInterval }) {
  const { data, ...rest } = useQuery(
    ['combinedOrderItems', { deliveryDayMarketplaceId, excludeOrdersInPacking }],
    () =>
      apiCalls
        .getCombinedOrderItems({ deliveryDayMarketplaceId, excludeOrdersInPacking })
        .then(({ data: { combinedOrderItems } }) => combinedOrderItems),
    {
      refetchInterval,
      enabled: !!deliveryDayMarketplaceId,
    }
  );
  return { combinedOrderItems: data, ...rest };
}

export function useCombinedOrderItemsForPacking({ orderIds, enabled, excludePackedOrders, onlyFulfilledOrderItems }) {
  const { data, ...rest } = useQuery(
    ['combinedOrderItems', { orderIds, excludePackedOrders }],
    () =>
      apiCalls
        .getCombinedOrderItems({ orderIds: orderIds.join(','), excludePackedOrders, onlyFulfilledOrderItems })
        .then(({ data: { combinedOrderItems } }) => combinedOrderItems),
    {
      enabled: !!orderIds?.length && enabled,
    }
  );
  return { combinedOrderItems: data, ...rest };
}

export function useOrders({ deliveryDayMarketplaceId, refetchInterval }) {
  const { data, ...rest } = useQuery(
    ['orders', { deliveryDayMarketplaceId }],
    () => apiCalls.getOrders({ deliveryDayMarketplaceId }).then(({ data: { orders } }) => orders),
    {
      enabled: !!deliveryDayMarketplaceId,
      refetchInterval,
    }
  );
  return { orders: data, ...rest };
}

export function useEditOrder({ deliveryDayMarketplaceId }) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation((params) => apiCalls.editOrder(params).then(({ data: { order } }) => order), {
    onSuccess: (order) => {
      const oldOrders = queryClient.getQueryData(['orders', { deliveryDayMarketplaceId }]);
      const newOrders = [...oldOrders];
      const orderIndex = oldOrders.findIndex((o) => o.id === order.id);
      if (orderIndex >= 0) {
        newOrders[orderIndex] = order;
      }

      queryClient.setQueryData(['orders', { deliveryDayMarketplaceId }], newOrders);
    },
  });
  return { editOrder: mutate, ...rest };
}

export function useEditDeliveryDayMarketplace() {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    (params) =>
      apiCalls
        .editDeliveryDayMarketplace(params)
        .then(({ data: { deliveryDayMarketplace } }) => deliveryDayMarketplace),
    {
      onSuccess: (ddm) => {
        queryClient.setQueryData(['deliveryDayMarketplace', { deliveryDayMarketplaceId: `${ddm.id}` }], ddm);
      },
    }
  );
  return { editDeliveryDayMarketplace: mutate, ...rest };
}

export function useRoutes({ deliveryDayId, version, customPackingType, showOrderInfo }) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useQuery(
    ['routes', { deliveryDayId, version }],
    () =>
      apiCalls
        .getRoutes({
          showOrderInfo,
          customPackingType,
          deliveryDayId,
          version,
        })
        .then(({ data: { routes } }) => routes),
    {
      enabled: !!deliveryDayId,
      onError: (error) => {
        queryClient.setQueryData(['routes', { deliveryDayId }], null);
      },
      refetchOnMount: true,
    }
  );
  return { routes: data, ...rest };
}

export function useRoute({ routeId }) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useQuery(
    ['route', { routeId }],
    () =>
      apiCalls
        .getRoutes({
          routeId,
        })
        .then(({ data: { routes } }) => routes[0]),
    {
      enabled: !!routeId,
      onError: (error) => {
        queryClient.setQueryData(['route', { routeId }], null);
      },
    }
  );
  return { route: data, ...rest };
}

export function useRouteEdit(onSuccess, onError) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ route }) => apiCalls.editRoute({ route }).then(({ data: { route } }) => route),
    {
      onSuccess: (route) => {
        queryClient.setQueryData(['route', { routeId: route.id.toString() }], route);
        onSuccess(route);
        return route;
      },
      onError: onError,
    }
  );
  return { editRoute: mutate, ...rest };
}

export function useDeliveryEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ delivery }) => apiCalls.editDelivery({ delivery }).then(({ data: { delivery } }) => delivery),
    {
      onSuccess: (delivery) => {
        queryClient.invalidateQueries('route');
        onSuccess(delivery);
        return delivery;
      },
    }
  );
  return { editDelivery: mutate, ...rest };
}
